import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'

export type StatTone = 'primary' | 'success' | 'warning' | 'info' | 'danger' | 'neutral'

const TONES: Record<StatTone, string> = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/12 text-warning',
    info: 'bg-info/10 text-info',
    danger: 'bg-destructive/10 text-destructive',
    neutral: 'bg-muted text-muted-foreground'
}

type StatTileProps = {
    label: string
    value: ReactNode
    hint?: ReactNode
    icon?: ReactNode
    tone?: StatTone
    href?: string
    className?: string
}

export const StatTile: FC<StatTileProps> = ({ label, value, hint, icon, tone = 'primary', href, className }): ReactNode => {
    const body = (
        <>
            {icon && (
                <span className={cn('grid size-11 shrink-0 place-items-center rounded-xl [&_svg]:size-5', TONES[tone])} aria-hidden='true'>
                    {icon}
                </span>
            )}

            <span className='min-w-0 flex-1'>
                <span className='block text-[13px] font-medium text-muted-foreground'>
                    {label}
                </span>

                <span className='mt-0.5 block text-2xl leading-tight font-bold tracking-tight text-foreground'>
                    {value}
                </span>

                {hint && (
                    <span className='mt-0.5 block truncate text-xs text-muted-foreground'>
                        {hint}
                    </span>
                )}
            </span>

            {href && (
                <ArrowUpRight
                    className='size-4 shrink-0 self-start text-muted-foreground transition-[opacity,transform] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary'
                    aria-hidden='true'
                />
            )}
        </>
    )

    const classes = cn('group flex min-w-0 items-center gap-4 rounded-xl border bg-card p-4 shadow-card', className)

    if (href) {
        return (
            <Link
                href={href}
                className={cn(classes, 'transition-[border-color,box-shadow,transform] outline-none hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-hover focus-visible:ring-3 focus-visible:ring-ring/40')}
            >
                {body}
            </Link>
        )
    }

    return <div className={classes}>{body}</div>
}
